// Content for the Grafodata website

export const companyInfo = {
  name: "Grafodata",
  tagline: "Cloud Architecture, DevOps & Cybersecurity for Industrial, Vehicle & Gaming Data",
  description:
    "Grafodata designs cloud infrastructure, container platforms, security architecture, and targeted automation that turn SCADA telemetry, ADAS/AV sensor data, and iGaming transaction data into operational intelligence.",
  email: "contact@grafodata.com",
};

export const heroData = {
  eyebrow: "Cloud Architecture · DevOps · Cybersecurity · Data Engineering",
  title: "Infrastructure Built for Data That Can't Go Down",
  subtitle:
    "We design cloud architecture, container platforms, and security for teams running SCADA telemetry, ADAS/AV sensor pipelines, and iGaming transaction data — with AI workflow automation applied where it actually saves engineering time, not everywhere.",
  primaryCta: "Talk to an Architect",
  secondaryCta: "See the Capabilities",
};

// ---------------------------------------------------------------------------
// Core capability pillars — the skills Grafodata leads with
// ---------------------------------------------------------------------------
export const capabilities = [
  {
    id: 1,
    slug: "infrastructure-as-code",
    title: "Infrastructure as Code",
    short: "Every environment versioned and reproducible",
    description:
      "Every environment we touch is defined, versioned, and reproducible — from a single edge site to a multi-region, multi-cloud fleet. No manual console changes that can't be traced or rolled back.",
    icon: "FileCode2",
    features: [
      "Terraform & Pulumi modules per environment",
      "Policy-as-code guardrails and drift detection",
      "Multi-cloud, multi-region reference architectures",
      "GitOps-driven change management and review",
      "Reusable modules for edge, OT, and plant-floor sites",
      "Automated environment teardown for cost control",
    ],
    industries: ["igaming", "battery-tech", "automotive"],
  },
  {
    id: 2,
    slug: "container-orchestration",
    title: "Container Orchestration",
    short: "Kubernetes sized for bursty and edge workloads",
    description:
      "Kubernetes and container platforms designed for the load pattern that actually matters — betting-day traffic spikes, distributed plant-floor edge nodes, or petabyte-scale ingestion pipelines.",
    icon: "Boxes",
    features: [
      "Kubernetes cluster design, hardening, and upgrades",
      "Autoscaling for peak-load events (match days, releases)",
      "Edge Kubernetes for distributed BESS and plant sites",
      "CI/CD pipelines for containerized workloads",
      "Service mesh, traffic shaping, and blue/green rollout",
      "Cost-aware node pools and storage tiering",
    ],
    industries: ["igaming", "battery-tech", "automotive"],
  },
  {
    id: 3,
    slug: "cybersecurity-compliance",
    title: "Cybersecurity & Compliance",
    short: "Security matched to how each system is attacked",
    description:
      "OT/ICS security is not cloud security is not fraud detection. We design the security model around how your specific system is actually regulated and attacked.",
    icon: "ShieldCheck",
    features: [
      "OT/IT network segmentation (IEC 62443-aligned)",
      "Cloud security posture management",
      "Fraud, anomaly, and responsible-gaming signal pipelines",
      "IAM and least-privilege access design",
      "Compliance evidence automation (PCI-DSS, GLI, ISO 27001)",
      "Incident response runbooks and tabletop exercises",
    ],
    industries: ["igaming", "battery-tech", "automotive"],
  },
  {
    id: 4,
    slug: "ai-workflow-automation",
    title: "AI Workflow Automation",
    short: "n8n automation aimed at specific bottlenecks",
    description:
      "AI and workflow automation, built on n8n, applied to specific well-defined bottlenecks — alert triage, compliance reporting, maintenance flags — not marketed as a blanket capability across everything.",
    icon: "Workflow",
    features: [
      "n8n workflow orchestration for ops & compliance tasks",
      "Automated alerting and ticket/case routing",
      "KYC/AML and case-management workflows (iGaming)",
      "Predictive maintenance alerting (battery / BESS)",
      "Edge-case triage and labeling hand-off (ADAS/AV)",
      "Human-in-the-loop approval flows for regulated actions",
    ],
    industries: ["igaming", "battery-tech", "automotive"],
  },
];

// ---------------------------------------------------------------------------
// Industries — who we serve and the specific data problem in each
// ---------------------------------------------------------------------------
export const industries = [
  {
    id: 1,
    slug: "igaming",
    name: "iGaming",
    fullName: "iGaming & Sports Betting",
    tagline: "Real-time risk, uptime, and compliance for regulated betting platforms",
    accent: "igaming",
    icon: "Dices",
    dataChallenge:
      "Betting platforms generate transaction, odds, and session data every millisecond, across dozens of licensing jurisdictions. Hosting partners handle uptime and bare-metal capacity — but real-time fraud detection, responsible-gaming signals, and multi-jurisdiction regulatory reporting are a separate engineering problem that usually gets bolted on late.",
    positioning:
      "We sit above the hosting layer: the data pipelines, security controls, and automation that turn raw betting traffic into fraud signals and compliance evidence — complementary to infrastructure providers, not a replacement for them.",
    pipeline: [
      { step: "Ingest", detail: "Bet slips, odds feeds, and wallet transactions from multiple platforms" },
      { step: "Stream", detail: "Kafka-based event backbone for sub-second processing" },
      { step: "Detect", detail: "Fraud and responsible-gaming anomaly scoring in real time" },
      { step: "Automate", detail: "n8n workflows for KYC checks, payout holds, and case routing" },
      { step: "Report", detail: "Jurisdiction-specific compliance exports, generated automatically" },
    ],
    compliance: ["PCI-DSS", "GLI-19", "Multi-jurisdiction licensing", "Responsible gambling (RG) signals"],
    outcomes: [
      { metric: "Sub-second", label: "fraud & RG signal scoring" },
      { metric: "Multi-region", label: "DR across licensed jurisdictions" },
      { metric: "Automated", label: "regulatory reporting exports" },
    ],
    relatedCapabilities: ["container-orchestration", "cybersecurity-compliance", "ai-workflow-automation"],
  },
  {
    id: 2,
    slug: "battery-tech",
    name: "Battery Tech",
    fullName: "Battery & Energy Storage",
    tagline: "SCADA and BMS telemetry, secured from factory floor to grid edge",
    accent: "battery",
    icon: "BatteryCharging",
    dataChallenge:
      "Battery manufacturers and BESS operators run OT systems — SCADA, BMS, PLCs — that were never designed to talk securely to the cloud. Every added sensor on a production line or a deployed storage site is another attack surface, and another data stream nobody is watching for drift or failure signatures.",
    positioning:
      "We build the segmented, auditable path from plant-floor and field telemetry into the cloud — so cell-level data becomes a predictive maintenance signal instead of an unmonitored liability.",
    pipeline: [
      { step: "Ingest", detail: "Cell/pack telemetry and PLC/SCADA tags via OPC-UA and MQTT" },
      { step: "Segment", detail: "OT/IT network separation aligned to IEC 62443" },
      { step: "Orchestrate", detail: "Edge Kubernetes for on-site preprocessing across sites" },
      { step: "Automate", detail: "n8n workflows for threshold alerts and maintenance ticketing" },
      { step: "Analyze", detail: "Cell-level anomaly detection and degradation modeling" },
    ],
    compliance: ["IEC 62443 (OT/ICS security)", "ISO 27001", "UL 1973 / UN 38.3 traceability", "Grid interconnection reporting"],
    outcomes: [
      { metric: "Segmented", label: "OT/IT telemetry pipeline" },
      { metric: "Fleet-wide", label: "site rollout via IaC" },
      { metric: "Early", label: "cell-degradation detection" },
    ],
    relatedCapabilities: ["infrastructure-as-code", "cybersecurity-compliance", "ai-workflow-automation"],
  },
  {
    id: 3,
    slug: "automotive",
    name: "Automotive",
    fullName: "Automotive, ADAS & AV",
    tagline: "Cloud backbone for petabyte-scale sensor data pipelines",
    accent: "automotive",
    icon: "Radar",
    dataChallenge:
      "A single test fleet can generate petabytes of camera, radar, lidar, and CAN data a month. Most of the cost and delay isn't the perception model — it's the storage tiering, ingestion pipelines, and access control around that data that never got engineered properly before annotation and validation began.",
    positioning:
      "We build the cloud and DevOps backbone that runs workloads like this — cost-optimized petabyte-scale storage, Kubernetes-based ingestion and labeling pipelines, and access control for fleet data — whether that sits behind an OEM's in-house platform or a data-curation vendor.",
    pipeline: [
      { step: "Ingest", detail: "Multi-sensor logs from fleet uplinks and depot transfers" },
      { step: "Orchestrate", detail: "Kubernetes-based ingestion, decoding, and labeling pipelines" },
      { step: "Optimize", detail: "Automated storage tiering for petabyte-scale multimodal data" },
      { step: "Secure", detail: "Fine-grained access control on fleet and PII-adjacent data" },
      { step: "Automate", detail: "n8n-driven scenario triage and labeling hand-off workflows" },
    ],
    compliance: ["Euro NCAP 2026 evidence trails", "GSR safety documentation", "ISO 26262 / ASPICE traceability", "Fleet data access governance"],
    outcomes: [
      { metric: "Petabyte-scale", label: "multimodal sensor ingestion" },
      { metric: "Lower cost", label: "via automated storage tiering" },
      { metric: "Automated", label: "compliance evidence trails" },
    ],
    relatedCapabilities: ["container-orchestration", "infrastructure-as-code", "cybersecurity-compliance"],
  },
];

// ---------------------------------------------------------------------------
// Use cases — concrete, illustrative pipelines per industry
// ---------------------------------------------------------------------------
export const useCases = [
  {
    id: 1,
    industry: "igaming",
    title: "Real-Time Fraud & RG Signal Pipeline",
    description:
      "A streaming pipeline that scores every transaction and session event for fraud and responsible-gaming risk as it happens, instead of in a nightly batch job.",
    outcome: "Sub-second anomaly scoring on live transaction streams",
    icon: "Activity",
  },
  {
    id: 2,
    industry: "igaming",
    title: "Multi-Jurisdiction Compliance Automation",
    description:
      "n8n workflows that assemble and route the right regulatory export for each licensed jurisdiction automatically, instead of a manual, error-prone spreadsheet process.",
    outcome: "Automated regulatory reporting across licensed states",
    icon: "ClipboardCheck",
  },
  {
    id: 3,
    industry: "battery-tech",
    title: "OT-to-Cloud SCADA Telemetry Pipeline",
    description:
      "A segmented, auditable data path from PLCs and BMS controllers to a cloud data lake, with OT/IT boundaries enforced at every hop.",
    outcome: "Auditable, segmented telemetry from plant floor to cloud",
    icon: "Radio",
  },
  {
    id: 4,
    industry: "battery-tech",
    title: "Fleet-Wide BESS Deployment via IaC",
    description:
      "Terraform modules that let a new storage site — residential, C&I, or grid-scale — be provisioned and monitored the same way every time.",
    outcome: "New site environments provisioned in hours, not weeks",
    icon: "GitBranch",
  },
  {
    id: 5,
    industry: "automotive",
    title: "Petabyte-Scale Sensor Data Lake",
    description:
      "Kubernetes-based ingestion with automated hot/warm/cold storage tiering for camera, radar, and lidar logs coming off a test fleet.",
    outcome: "Meaningful storage cost reduction via automated tiering",
    icon: "Database",
  },
  {
    id: 6,
    industry: "automotive",
    title: "Automated Edge-Case Triage with n8n",
    description:
      "Workflow automation that flags high-value, high-risk driving scenarios and routes them to the right labeling or review queue automatically.",
    outcome: "Faster hand-off from data collection to labeling teams",
    icon: "Workflow",
  },
];

export const differentiators = [
  {
    id: 1,
    title: "Industry-Fluent, Not Generic",
    description:
      "SCADA security, ADAS data governance, and iGaming compliance are different disciplines. We design for the one you're actually in, instead of a one-size-fits-all cloud playbook.",
  },
  {
    id: 2,
    title: "Infrastructure as the Foundation",
    description:
      "IaC and container orchestration aren't an afterthought. Every pipeline and environment is versioned, reproducible, and built to survive an audit.",
  },
  {
    id: 3,
    title: "Automation Where It Earns Its Keep",
    description:
      "AI and workflow automation (n8n) get applied to specific, well-defined problems — alerting, triage, compliance reporting — not marketed as a blanket capability.",
  },
  {
    id: 4,
    title: "Security Matched to the Threat Model",
    description:
      "OT/IT segmentation for industrial systems, fraud detection for transactional systems, access governance for fleet data — each industry gets the model it actually needs.",
  },
];

export const outcomes = [
  { id: 1, metric: "3", label: "Industries, One Engineering Discipline" },
  { id: 2, metric: "4", label: "Core Capabilities: IaC, Containers, Security, Automation" },
  { id: 3, metric: "Multi-Cloud", label: "AWS, GCP, and Azure Architecture" },
  { id: 4, metric: "24/7", label: "Infrastructure & Security Monitoring" },
];

export const techStack = [
  { name: "AWS", category: "cloud" },
  { name: "Google Cloud", category: "cloud" },
  { name: "Azure", category: "cloud" },
  { name: "Kubernetes", category: "orchestration" },
  { name: "Docker", category: "orchestration" },
  { name: "Terraform", category: "iac" },
  { name: "Ansible", category: "iac" },
  { name: "n8n", category: "automation" },
  { name: "Cloudflare", category: "security" },
  { name: "Apache Kafka", category: "data" },
];

export const aboutData = {
  mission:
    "To give engineering-heavy, regulated industries the cloud architecture, automation, and security discipline their data actually needs.",
  philosophy: [
    {
      title: "Infrastructure as Code, By Default",
      description:
        "Every environment we touch gets versioned, reviewable, and reproducible — no manual console changes that can't be traced or rolled back.",
    },
    {
      title: "Automation With a Target",
      description:
        "We deploy AI and workflow automation (n8n) against specific bottlenecks — alert triage, compliance reporting, maintenance flags — not as a blanket label for the whole practice.",
    },
    {
      title: "Security Matched to the Threat Model",
      description:
        "OT/ICS security is not cloud security is not fraud detection. We design each engagement around how that specific system actually gets attacked.",
    },
    {
      title: "Depth Over Breadth",
      description:
        "Three industries, not thirty. Going deep on iGaming, battery/energy, and automotive data means pattern-matched solutions instead of generic playbooks.",
    },
  ],
  whyWeExist:
    "Most cloud consultancies sell the same generic stack to every client. But SCADA telemetry, ADAS sensor logs, and iGaming transaction data are not the same problem — they carry different compliance regimes, different threat models, and different scale patterns. Grafodata exists to bring real infrastructure discipline — IaC, container orchestration, security architecture, and targeted automation — to teams building on industrial, vehicle, and gaming data.",
  credentials: [
    { icon: "Award", label: "Cloud Architecture", value: "AWS, GCP & Azure" },
    { icon: "Boxes", label: "Orchestration", value: "Kubernetes & Docker" },
    { icon: "ShieldCheck", label: "Security", value: "OT/ICS & Cloud Posture" },
    { icon: "Workflow", label: "Automation", value: "n8n Workflow Design" },
  ],
};

export const navLinks = [
  { name: "Home", path: "/" },
  { name: "Industries", path: "/industries" },
  { name: "Capabilities", path: "/capabilities" },
  { name: "Use Cases", path: "/use-cases" },
  { name: "About", path: "/about" },
];

export const footerLinks = {
  industries: [
    { name: "iGaming", path: "/industries/igaming" },
    { name: "Battery Tech", path: "/industries/battery-tech" },
    { name: "Automotive & ADAS", path: "/industries/automotive" },
  ],
  capabilities: [
    { name: "Infrastructure as Code", path: "/capabilities#infrastructure-as-code" },
    { name: "Container Orchestration", path: "/capabilities#container-orchestration" },
    { name: "Cybersecurity & Compliance", path: "/capabilities#cybersecurity-compliance" },
    { name: "AI Workflow Automation", path: "/capabilities#ai-workflow-automation" },
  ],
  company: [
    { name: "About", path: "/about" },
    { name: "Use Cases", path: "/use-cases" },
    { name: "Contact", path: "/contact" },
  ],
};
